import React from "react";
import styled from "@emotion/styled";
import { MoreVertical } from "react-feather";

import {
  Card as MuiCard,
  CardHeader,
  IconButton,
  Chip as MuiChip,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import { spacing } from "@mui/system";
import { tableCellClasses } from "@mui/material/TableCell";

const Card = styled(MuiCard)(spacing);

const Chip = styled(MuiChip)`
  height: 20px;
  padding: 4px 0;
  font-size: 90%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : "primary"].light};
  color: ${(props) => props.theme.palette.common.white};
`;

const Paper = styled(MuiPaper)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)});
`;

const CustomTableCell = styled(TableCell)`
  &.${tableCellClasses.head} {
    background: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.palette.common.white};
  }
  &.${tableCellClasses.body} {
    font-size: 14px;
  }
`;

const CustomTableRow = styled(TableRow)`
  &:nth-of-type(odd) {
    background-color: rgba(0, 0, 0, 0.025);
  }
`;

/**
 * CommonTable - A reusable table component
 *
 * @param {Object} props
 * @param {string} props.title - Table title
 * @param {string} props.subtitle - Table subtitle or description
 * @param {Array} props.columns - Array of column definitions: [{id: 'name', label: 'Name', align: 'left'}]
 * @param {Array} props.data - Array of data objects
 * @param {boolean} props.customized - Use custom styled header and rows
 * @param {boolean} props.showHeader - Show the CardHeader with title and actions
 * @param {boolean} props.showTableHeader - Show the table header row with column names
 * @param {Function} props.renderCellContent - Function to render cell content (row, column, index)
 * @param {React.Node} props.actions - Custom actions to display in header
 * @param {Object} props.cardProps - Additional props for the Card component
 * @param {string} props.height - Fixed height for the table
 */
const CommonTable = ({
  title,
  subtitle,
  columns = [],
  data = [],
  customized = false,
  showHeader = true,
  showTableHeader = true,
  renderCellContent,
  actions,
  cardProps = {},
  height,
}) => {
  const TableComponent = customized ? CustomTableRow : TableRow;
  const CellComponent = customized ? CustomTableCell : TableCell;

  // Default table cell renderer
  const defaultRenderCellContent = (row, column, index) => {
    return row[column.id];
  };

  // Use custom renderer or the default one
  const cellRenderer = renderCellContent || defaultRenderCellContent;

  // Default action button
  const defaultActions = (
    <IconButton aria-label="settings" size="large">
      <MoreVertical />
    </IconButton>
  );

  return (
    <Card mb={6} {...cardProps}>
      {showHeader && (
        <CardHeader
          action={actions || defaultActions}
          title={title || "Table"}
          subheader={subtitle}
        />
      )}
      <Paper>
        <TableWrapper style={height ? { height, overflowY: "auto" } : {}}>
          <Table>
            {showTableHeader && (
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <CellComponent
                      key={column.id || index}
                      align={column.align || "left"}
                      style={column.style || {}}
                      width={column.width}
                    >
                      {column.label}
                    </CellComponent>
                  ))}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {data.length > 0 ? (
                data.map((row, rowIndex) => (
                  <TableComponent
                    key={row.id || rowIndex}
                    hover={!customized}
                    onClick={row.onClick}
                    style={row.style || {}}
                  >
                    {columns.map((column, colIndex) => (
                      <CellComponent
                        key={column.id || colIndex}
                        component={colIndex === 0 ? "th" : "td"}
                        scope={colIndex === 0 ? "row" : null}
                        align={column.align || "left"}
                        style={column.cellStyle || {}}
                      >
                        {cellRenderer(row, column, colIndex)}
                      </CellComponent>
                    ))}
                  </TableComponent>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    style={{ padding: "24px" }}
                  >
                    <Typography variant="body1" color="textSecondary">
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </Paper>
    </Card>
  );
};

export default CommonTable;
